import { event } from "react-ga";

import React from "react";
import _ from "lodash";

import { Song } from "./types/song";
import { GuessType } from "./types/guess";

import { getGameKey, getTodaysSolution, validateSongLibrary } from "./helpers";
import { playTimes, songs } from "./constants";

import { AdminPage, Header, InfoPopUp, Game, SeoContent } from "./components";

import * as Styled from "./app.styled";

const initialGuesses = (): GuessType[] => {
  const initialGuess = {
    song: undefined,
    skipped: false,
    isCorrect: undefined,
  } as GuessType;

  return Array.from({ length: playTimes.length }).fill(
    initialGuess
  ) as GuessType[];
};

type SavedGames = Record<
  string,
  {
    currentTry: number;
    didGuess: boolean;
    guesses: GuessType[];
  }
>;

function loadSavedGames(): SavedGames {
  try {
    const savedGames = JSON.parse(localStorage.getItem("swiftleStats") || "{}");

    return savedGames && typeof savedGames === "object" ? savedGames : {};
  } catch {
    return {};
  }
}

function GameApp() {
  const [songLibrary, setSongLibrary] = React.useState<Song[]>(songs);
  const [guesses, setGuesses] = React.useState<GuessType[]>(initialGuesses);
  const [currentTry, setCurrentTry] = React.useState<number>(0);
  const [selectedSong, setSelectedSong] = React.useState<Song>();
  const [selectionMessage, setSelectionMessage] = React.useState<string>("");
  const [didGuess, setDidGuess] = React.useState<boolean>(false);

  const todaysSolution = React.useMemo(
    () => getTodaysSolution(songLibrary),
    [songLibrary]
  );
  const gameKey = React.useMemo(
    () => getGameKey(todaysSolution),
    [todaysSolution]
  );
  const firstRun = localStorage.getItem("firstRun") === null;

  React.useEffect(() => {
    let isMounted = true;

    fetch("/api/library")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load song library");
        }

        return response.json();
      })
      .then((data) => {
        const validation = validateSongLibrary(data.songs);

        if (isMounted && validation.ok) {
          setSongLibrary(validation.songs);
        }
      })
      .catch(() => {
        // Keep the built-in library when the shared API is unavailable.
      });

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    const savedGame = loadSavedGames()[gameKey];

    if (savedGame) {
      setCurrentTry(savedGame.currentTry);
      setGuesses(savedGame.guesses);
      setDidGuess(savedGame.didGuess);
    } else {
      setCurrentTry(0);
      setGuesses(initialGuesses());
      setDidGuess(false);
    }

    setSelectedSong(undefined);
    setSelectionMessage("");
  }, [gameKey]);

  React.useEffect(() => {
    const savedGames = loadSavedGames();

    savedGames[gameKey] = {
      currentTry,
      didGuess,
      guesses,
    };

    localStorage.setItem("swiftleStats", JSON.stringify(savedGames));
  }, [gameKey, guesses, currentTry, didGuess]);

  React.useEffect(() => {
    try {
      const legacyStats = JSON.parse(localStorage.getItem("stats") || "{}");

      if (Array.isArray(legacyStats)) {
        const visitedToday = _.isEqual(
          todaysSolution,
          legacyStats[legacyStats.length - 1]?.solution
        );

        if (visitedToday) {
          const {
            currentTry: savedCurrentTry,
            guesses: savedGuesses,
            didGuess: savedDidGuess,
          } = legacyStats[legacyStats.length - 1];

          setCurrentTry(savedCurrentTry);
          setGuesses(savedGuesses);
          setDidGuess(Boolean(savedDidGuess));
        }
      }
    } catch {
      // Ignore malformed legacy stats.
    }
  }, [todaysSolution]);

  const [isInfoPopUpOpen, setIsInfoPopUpOpen] =
    React.useState<boolean>(firstRun);

  const openInfoPopUp = React.useCallback(() => {
    setIsInfoPopUpOpen(true);
  }, []);

  const closeInfoPopUp = React.useCallback(() => {
    if (firstRun) {
      localStorage.setItem("firstRun", "false");
      setIsInfoPopUpOpen(false);
    } else {
      setIsInfoPopUpOpen(false);
    }
  }, [localStorage.getItem("firstRun")]);

  const skip = React.useCallback(() => {
    setSelectedSong(undefined);
    setSelectionMessage("");

    setGuesses((guesses: GuessType[]) => {
      const newGuesses = [...guesses];
      newGuesses[currentTry] = {
        song: undefined,
        skipped: true,
        isCorrect: undefined,
      };

      return newGuesses;
    });

    setCurrentTry((currentTry) => currentTry + 1);

    event({
      category: "Game",
      action: "Skip",
    });
  }, [currentTry]);

  const guess = React.useCallback(() => {
    if (!selectedSong) {
      setSelectionMessage("请先从搜索结果中选择一首歌。");
      return;
    }

    const isCorrect = selectedSong.youtubeId === todaysSolution.youtubeId;

    setGuesses((guesses: GuessType[]) => {
      const newGuesses = [...guesses];
      newGuesses[currentTry] = {
        song: selectedSong,
        skipped: false,
        isCorrect: isCorrect,
      };

      return newGuesses;
    });

    setCurrentTry((currentTry) => currentTry + 1);
    setSelectedSong(undefined);
    setSelectionMessage("");

    if (isCorrect) {
      setDidGuess(true);
    }

    event({
      category: "Game",
      action: "Guess",
      label: `${selectedSong.artist} - ${selectedSong.name}`,
      value: isCorrect ? 1 : 0,
    });
  }, [currentTry, selectedSong, todaysSolution]);

  const chooseSong = React.useCallback((song: Song | undefined) => {
    setSelectedSong(song);
    setSelectionMessage("");
  }, []);

  return (
    <main>
      <Header openInfoPopUp={openInfoPopUp} />
      {isInfoPopUpOpen && <InfoPopUp onClose={closeInfoPopUp} />}
      <Styled.Container>
        <Game
          guesses={guesses}
          songs={songLibrary}
          didGuess={didGuess}
          todaysSolution={todaysSolution}
          currentTry={currentTry}
          selectionMessage={selectionMessage}
          setSelectedSong={chooseSong}
          skip={skip}
          guess={guess}
        />
      </Styled.Container>
      <SeoContent />
    </main>
  );
}

function App() {
  if (window.location.pathname.replace(/\/$/, "") === "/admin") {
    return <AdminPage />;
  }

  return <GameApp />;
}

export default App;
